var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Main proposal form component.
 *
 * <p>Renders the tabbed form for creating/editing authority-record proposals
 * entirely from the compiled Effective Form Config (EFC) via {@link RecordForm}
 * — there is exactly ONE form engine (CZNA-487). The hardcoded `LegacyForm`
 * that used to serve record types without a form-config was deleted once
 * CZNA-458 + CZNA-491 gave every `RecordType` a packaged config; what is left
 * when a config genuinely cannot be resolved is an explicit error state, not a
 * second, differently-shaped form (see {@link FormConfigUnavailable}).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { Pane, Paneset, PaneFooter, Button, Loading, MessageBanner, Callout, } from '@folio/stripes/components';
import { ProposalStatus, ProposalType, PENDING_PROPOSAL_STATUSES, isTwoStageApproval, publishesImmediately } from '../../types/api';
import { createProposal, updateProposal, approveProposal, rejectProposal, publishProposal, submitProposal, fetchProposalDetail, } from '../../api/proposalService';
import { validateRecord, parseValidationFailedError } from '../../api/validationService';
import { proposalDataToMarc } from '../../utils/recordToMarc';
import { ValidationIssueGroups } from '../Validation/ValidationIssueGroups';
import { CheckBadge } from '../Validation/CheckBadge';
import { CheckPanel } from '../Validation/CheckPanel';
import RejectRequestChangesButtons from '../ProposalDetail/RejectRequestChangesButtons';
import { getErrorInfo } from '../../utils/getErrorCode';
import { getProposalErrorMessageId } from '../../utils/getErrorMessageId';
import { getErrorKind, isTimeoutError } from '../../utils/errorKind';
import { PROPOSALS_LIST_HREF, saveOutcomeUnknownError, verificationFromVersions, } from '../../utils/saveOutcomeUnknown';
import { secondLegMessageIdForKind } from '../../utils/secondLegMessageId';
import { proposalDataToGenericFormState } from './utils/proposalDataToFormState';
import { getDefaultGenericFormState } from './formTypes';
import { withTabsCarryingErrors } from './proposeAuthorityFlow';
import { describeFieldLocation, shownTabs } from './utils/locateField';
import { parseSummarizedValue } from './codec/codecRegistry';
import useFormConfig from '../../hooks/useFormConfig';
import useAuthorizationContext from '../../hooks/useAuthorizationContext';
import { withCatalogingSourceSigla, isFoundingForm } from './config/catalogingSourceSigla';
import { controlFieldRow, extractControlFields } from '../../utils/marcControlFields';
import { proposalDataToRecordDataFields } from './utils/proposalDataToRecordDataFields';
import { genericFormToProposalData } from './utils/genericFormToProposalData';
import RecordForm from './RecordForm';
import { PROPOSALS_QUERY_ROOT } from '../../hooks/useProposalList';
import { recordDisplayId } from '../../utils/recordDisplayId';
import { useBackLinkOffers } from '../BackLinkOffer/useBackLinkOffers';
/**
 * CZNA-1518 — a verdict with both issue lists and both counts. The module serialises with
 * `non_null`, so a list it ever left null arrives as no key at all, and the tab counts, the
 * header badge and the panel all read the lists whole: one missing key took the form down
 * on the render after the check, where the old banner had only read `errorCount`.
 */
var completeVerdict = function (result) {
    var _a, _b, _c, _d;
    var errors = (_a = result.errors) !== null && _a !== void 0 ? _a : [];
    var warnings = (_b = result.warnings) !== null && _b !== void 0 ? _b : [];
    return __assign(__assign({}, result), { errors: errors, warnings: warnings, errorCount: (_c = result.errorCount) !== null && _c !== void 0 ? _c : errors.length, warningCount: (_d = result.warningCount) !== null && _d !== void 0 ? _d : warnings.length });
};
/**
 * Config-driven proposal form wrapper.
 * Handles proposal-specific save logic (API calls, navigation, error resolution)
 * and delegates all rendering to the generic RecordForm component.
 */
var ConfigDrivenProposalForm = function (_a) {
    var _b, _c, _d, _e, _f;
    var recordType = _a.recordType, onCancel = _a.onCancel, mode = _a.mode, proposalId = _a.proposalId, initialData = _a.initialData, proposalStatus = _a.proposalStatus, proposalType = _a.proposalType, returnListUrlOverride = _a.returnListUrl, targetRecordAuthorityId = _a.targetRecordAuthorityId, targetRecordId = _a.targetRecordId, targetRecordVersion = _a.targetRecordVersion, targetRecordExportId = _a.targetRecordExportId, optimisticLock = _a.optimisticLock, metadata = _a.metadata, reviewMode = _a.reviewMode, _g = _a.canApprove, canApprove = _g === void 0 ? true : _g, seedHeading = _a.seedHeading, onCreated = _a.onCreated, embedded = _a.embedded, requestCloseRef = _a.requestCloseRef, restoreKey = _a.restoreKey, publishOnCreate = _a.publishOnCreate, visibleTabIds = _a.visibleTabIds, notice = _a.notice, config = _a.config;
    var intl = useIntl();
    var history = useHistory();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var isEdit = mode === 'edit' && !!proposalId;
    // CZNA-1409 — the back links a save could add, answered before the form moves on.
    var _h = useBackLinkOffers(), offerBackLinks = _h.offerBackLinks, backLinkOfferModal = _h.backLinkOfferModal;
    // TASK-159 / AV-01: client-side validation preflight. The outcome is reported by the check
    // counts in the pane header and the panel they open; the only dialog that shows it is the
    // Publikovat/Odeslat confirm, where it is part of a decision (CZNA-1518 — the "Uložit
    // přesto" warning modal is gone from here).
    var _j = useState(null), validationResult = _j[0], setVerdict = _j[1];
    var setValidationResult = function (result) {
        setVerdict(result && completeVerdict(result));
    };
    /**
     * CZNA-785 — the save confirmation, as a Stripes Callout (the transient bubble FOLIO
     * uses everywhere else for "this happened"), not a banner in the form header. NK asked
     * for the standard control after seeing the banner: it sat in the heading, where it read
     * as part of the form rather than as the outcome of pressing Save.
     *
     * Deliberately NOT derived from navigation. Three of the four save outcomes keep the
     * form mounted on purpose (blocking errors, needsReview, and the picker's in-tab flow),
     * so any acknowledgement tied to leaving the page cannot reach them — and those are the
     * cases a cataloguer is most likely to be sitting in, because they are the ones that
     * leave work to finish.
     *
     * Same idiom as `ModuleSettingEditor` / `RecordForm`: a ref'd `<Callout>` plus
     * `sendCallout`. The `Record<string, any>` ref type is the one Stripes' untyped
     * component forces (see RecordForm.tsx:202).
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var calloutRef = useRef(null);
    /**
     * CZNA-1450 — tells RecordForm which values a save that HOLDS the form open just wrote,
     * so they stop counting as unsaved. See the prop's own doc on `RecordFormProps`.
     */
    var markSavedRef = useRef(null);
    var jumpToFieldRef = useRef(null);
    // CZNA-1518 — the stale check counts re-run Zkontrolovat, which lives in RecordForm.
    var runCheckRef = useRef(null);
    var checkBadgeRef = useRef(null);
    // CZNA-1518 — `findings`: what the check found in the draft just saved. One sentence in
    // the same bubble rather than a second one: the news is that the write landed, and where
    // the list is. The panel does not open on its own here — the cataloguer pressed Uložit to
    // keep the work, not to read about it.
    var confirmSaved = function (authorityId, findings) {
        var _a;
        var saved = intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.savedAs' }, { authorityId: authorityId !== null && authorityId !== void 0 ? authorityId : '' });
        (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
            type: 'success',
            message: findings ? "".concat(saved, " ").concat(findings) : saved,
        });
    };
    // CZNA-582: which of the two things the check panel is reporting — "the draft is
    // saved, these are what still stand between it and a submit" (true) or "the action
    // you asked for was refused" (false). Same issue list, opposite news; a heading that
    // says "fix these before saving" over a draft that IS saved teaches the cataloguer to
    // distrust it.
    var _k = useState(false), savedWithErrors = _k[0], setSavedWithErrors = _k[1];
    // CZNA-1518: a draft saved with warnings only. It used to wait behind "Uložit přesto?";
    // a draft is nobody's but its author's, and the stronger finding (an error) never asked.
    var _l = useState(false), savedWithWarnings = _l[0], setSavedWithWarnings = _l[1];
    // CZNA-1518: the warnings of the check that opened the Publikovat/Odeslat confirm, shown
    // inside it. Shown only after the "Ano" they used to come up as a second dialog.
    var _m = useState(null), confirmWarnings = _m[0], setConfirmWarnings = _m[1];
    // CZNA-1518: when the errors stopped a Publikovat/Odeslat click (or came from
    // Zkontrolovat), which one — so the panel's heading says what did NOT happen, and that
    // nothing was saved. `null` for every other source of a verdict.
    var _o = useState(null), blockedAction = _o[0], setBlockedAction = _o[1];
    // CZNA-1518: the check panel. Opened by the counts in the pane header, and by itself when
    // a check the cataloguer asked for has something to say: Zkontrolovat always (a check that
    // answers only when something is wrong looks, on a clean record, like a button that does
    // nothing), Publikovat/Odeslat when an error stopped them.
    var _p = useState(false), checkPanelOpen = _p[0], setCheckPanelOpen = _p[1];
    // CZNA-1518: the form has changed since the verdict on display. Nothing is checked while
    // the cataloguer types; the counts turn grey and the next click on them checks again.
    var _q = useState(false), checkStale = _q[0], setCheckStale = _q[1];
    // CZNA-411 (WP-P3c): non-blocking server-recompute warnings from the last
    // successful save. The save has already succeeded (201/200) by the time
    // these are set — this is informational only, never a save gate.
    var _r = useState([]), needsReviewItems = _r[0], setNeedsReviewItems = _r[1];
    // CZNA-411: whenever a save comes back with needsReview, we hold the form
    // open (below) instead of auto-navigating/auto-invalidating so its banner
    // stays visible. `proposalId`/`optimisticLock` are props and cannot be
    // reassigned, so this snapshot is the local override for BOTH cases that
    // can follow such a save:
    //  - CREATE_NEW: no `proposalId` prop exists yet — without this, a second
    //    save would call createProposal again (POST), creating a duplicate.
    //  - UPDATE (isEdit already true): `optimisticLock` increments server-side
    //    on every save; without tracking the new value, a second save would
    //    send the now-stale prop value and 409-conflict.
    var _s = useState(null), savedSnapshot = _s[0], setSavedSnapshot = _s[1];
    // The validate-record endpoint expects only the validator's RecordType enum.
    // Since CZNA-487 every `RecordType` routes here (they all have a packaged
    // form-config), and `RecordType` is that same enum on the wire.
    var validationRecordType = recordType;
    // TASK-101: form runs in 'update' mode for UPDATE_EXISTING proposals (an
    // existing record is being amended) and 'create' mode otherwise. Drives
    // FieldDef.hideOnCreate filtering — field 001 (authority number) is hidden
    // in create mode and shown read-only with the target record's id in update
    // mode.
    var formMode = proposalType === 'UPDATE_EXISTING' ? 'update' : 'create';
    var convertedInitialData = initialData && initialData.length > 0
        ? proposalDataToGenericFormState(config, initialData)
        : getDefaultGenericFormState(config);
    // CZNA-197: when opened from the picker with a seed (and no pre-existing data),
    // pre-fill the primary heading subfield (1XX $a). This is now the ONLY seeding
    // path — the legacy form's `mainHeading` slot went away with it (CZNA-487).
    if (seedHeading && (!initialData || initialData.length === 0)) {
        for (var _i = 0, _t = config.tabs; _i < _t.length; _i++) {
            var tab = _t[_i];
            var headingField = tab.fields.find(function (fd) { return fd.tag.startsWith('1') && fd.subfields.some(function (sf) { return sf.code === 'a'; }); });
            if (headingField) {
                var key = headingField.fieldId || headingField.tag;
                var firstInstance = (_b = convertedInitialData.fields[key]) === null || _b === void 0 ? void 0 : _b[0];
                if (firstInstance && firstInstance.a) {
                    firstInstance.a = __assign(__assign({}, firstInstance.a), { value: seedHeading });
                    // CZNA-321: a component heading (PERSONAL `100$a`: Příjmení / Jméno) is
                    // SAVED from its components, not from `value` — `genericFormToProposalData`
                    // drops a component subfield whose components are all empty. So a seed
                    // written only to `value` showed empty inputs and vanished on save.
                    // Decomposed with the same codec the load path uses for a flat
                    // imported value (CZNA-310), under the instance's own ind1.
                    var headingSubfield = headingField.subfields.find(function (sf) { return sf.code === 'a'; });
                    if ((headingSubfield === null || headingSubfield === void 0 ? void 0 : headingSubfield.hasComponents) && headingSubfield.components && headingSubfield.summarizeFn) {
                        var parsed = parseSummarizedValue(headingSubfield, seedHeading, (_c = firstInstance._ind1) === null || _c === void 0 ? void 0 : _c.value);
                        firstInstance.a = __assign(__assign({}, firstInstance.a), { components: __assign(__assign({}, ((_d = firstInstance.a.components) !== null && _d !== void 0 ? _d : {})), parsed) });
                    }
                }
                break;
            }
        }
    }
    // TASK-101: the authority number (field 001 $a) for an UPDATE_EXISTING
    // proposal is shown read-only at render time only — it is NOT pushed into
    // react-hook-form state. This keeps `proposal_data` clean: since
    // `genericFormToProposalData` iterates `formData.fields`, an unregistered
    // 001 simply never appears in the saved payload. exportId is immutable
    // post-approval anyway, but the architectural property we want is that the
    // form never re-emits 001 just because it was rendered. The display value
    // is threaded down via `targetRecordAuthorityIdDisplay` (see RecordForm →
    // DynamicTab).
    //
    // CZNA-504 deliberately does NOT feed this value to the MARC preview
    // (`previewControlFields`), even though an UPDATE_EXISTING preview without
    // 001 is an incomplete record. `targetRecordAuthorityId` is the target's
    // `authority_id` (ProposalMapperImpl) — the internal `AUT-…` handle, not its
    // MARC 001, which is `export_id` (`xx0023870` on an Aleph-imported record).
    // The comment above calls the two interchangeable "for non-imported
    // records"; the corpus is 1.27 M imported ones, where they differ. Rendering
    // it under a `001` badge would put a value in the preview that is not in the
    // record. That still holds, and is why the block below shows 001 as PENDING
    // rather than as a number.
    //
    // CZNA-684 changes the other half of that reasoning. The preview is no longer
    // control-field-free on the proposal path, because a proposal now really does carry
    // an 008 (the system stamps it at create/update/submit). Two sources here, and a
    // third one deliberately elsewhere:
    //
    //   1. control fields the proposal ALREADY has — the truth, once saved;
    //   2. what will be ASSIGNED on save (001) — pending text, never a number. Reserving
    //      an identifier for a draft that may never be published would burn it (Martin,
    //      24. 8.), and inventing one for display would reintroduce exactly the
    //      preview-lies-about-the-record problem this is fixing.
    //   3. what the backend will DERIVE (008) is fetched by `MarcPreviewPanel` itself,
    //      from the LIVE form state, because the derivation reads the record and not the
    //      record type — see the `recordType` prop there. Asking from here would mean
    //      asking about the form's INITIAL state, which on a new proposal is empty: no
    //      1XX means `AlephFormat` resolves to XX and no `040 $e` means the rda flag is
    //      never set, so the answer would differ from the stored 008 in four positions.
    //
    // Display-only throughout: `previewControlFields` never enters react-hook-form state
    // — it is a separate prop that no save path reads (`handleSave` builds its payload
    // from `genericFormToProposalData(config, formData)` alone) — so the pending
    // placeholder text cannot be persisted. Note this is NOT because the form refuses to
    // emit control fields: since CZNA-657 `genericFormToProposalData` does re-emit them,
    // as passthrough, which is how an existing 008 survives a full-replace PUT.
    var existingControlFields = extractControlFields(proposalDataToRecordDataFields(initialData !== null && initialData !== void 0 ? initialData : []));
    var pendingAssignmentLabel = intl.formatMessage({
        id: 'ui-czna-authorities.proposalForm.marcPreview.assignedOnSave',
    });
    // Extracted so the memo's dependency array is statically checkable: the input is a
    // freshly-built array on every render, so identity is useless and content is what
    // matters.
    var existingControlFieldsKey = JSON.stringify(existingControlFields);
    var previewControlFields = useMemo(function () {
        var present = new Set(existingControlFields.map(function (f) { return f.fieldTag; }));
        var rows = __spreadArray([], existingControlFields, true);
        // 001 only on the CREATE_NEW path. On UPDATE_EXISTING the target record already
        // has one and "will be assigned" would be false; it is simply not available here
        // (see above), so that path keeps showing no 001 rather than a wrong claim.
        if (formMode === 'create' && !present.has('001')) {
            rows.push(controlFieldRow('001', pendingAssignmentLabel));
        }
        return rows;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingControlFieldsKey, formMode, pendingAssignmentLabel]);
    // Return URL preserves the source review tab so the user stays in context
    // (PENDING_REVIEW → Ke schválení — nové/aktualizace, PENDING_NATIONAL_REVIEW → Ke
    // schválení v NK, otherwise default Koncepty).
    var returnListUrl = (function () {
        if (returnListUrlOverride) {
            return returnListUrlOverride;
        }
        if (proposalStatus === ProposalStatus.PENDING_REVIEW) {
            var tab = proposalType === 'UPDATE_EXISTING' ? 'PENDING_UPDATE' : 'PENDING_NEW';
            return "/czna-authorities/proposals?tab=".concat(tab);
        }
        // CZNA-552 — the national queue is not split by proposalType, so there is one tab.
        if (proposalStatus === ProposalStatus.PENDING_NATIONAL_REVIEW) {
            return '/czna-authorities/proposals?tab=PENDING_NATIONAL';
        }
        return '/czna-authorities/proposals';
    })();
    // CZNA-411: fall back to the locally-tracked id once a CREATE_NEW save has
    // produced needsReview (see `savedSnapshot` above) — subsequent clean
    // saves on the same mount still deep-link to the right detail pane.
    var effectiveProposalId = (_e = savedSnapshot === null || savedSnapshot === void 0 ? void 0 : savedSnapshot.id) !== null && _e !== void 0 ? _e : proposalId;
    // CZNA-763: the same override for the lock version. Hoisted out of `performSave` because
    // save+approve and save+reject write through `updateProposal` too — reading the bare prop
    // there meant that after ANY save that holds the form open (needsReview, or a draft saved
    // with check findings — CZNA-582, CZNA-1518) those two buttons sent a version the row had already
    // moved past, and 409'd on the caller's own previous save. One expression, all five callers.
    var effectiveOptimisticLock = (_f = savedSnapshot === null || savedSnapshot === void 0 ? void 0 : savedSnapshot.optimisticLock) !== null && _f !== void 0 ? _f : optimisticLock;
    var returnDetailUrl = effectiveProposalId ? "".concat(returnListUrl.split('?')[0], "/").concat(effectiveProposalId).concat(returnListUrl.includes('?') ? returnListUrl.substring(returnListUrl.indexOf('?')) : '') : returnListUrl;
    // CZNA-241: where the GA create route navigates BACK to, so this parent form
    // re-mounts and rehydrates. For an EDIT-mode parent it's the edit route; for a
    // CREATE-mode parent it carries `?type=<recordType>` so ProposalCreate skips
    // the AuthorityTypeDialog and renders the same form on return. The picker flow
    // is only meaningful for the config-driven parents that host a 370 picker
    // (PERSONAL / CORPORATE / MEETING).
    //
    // CZNA-321: a CREATE mount that has already saved (held open by needsReview, CZNA-411)
    // IS that proposal now — its id lives only in `savedSnapshot`, which the flow's
    // sessionStorage snapshot does not carry and a remount on return drops. Returning to
    // the create route would therefore make the next save a second POST, a duplicate
    // proposal. Returning to the saved proposal's edit route reloads it with its current
    // optimistic lock, and the restore effect lays the in-progress values over it.
    var proposeReturnTo = (isEdit || savedSnapshot) && effectiveProposalId
        ? "/czna-authorities/proposals/".concat(effectiveProposalId, "/edit")
        : "/czna-authorities/proposals/create?type=".concat(recordType);
    /**
     * Inner save chain — the actual write to the proposals endpoint. Wrapped by
     * {@link handleSave} which interposes the validation preflight + WARNING modal.
     *
     * `options.then` is the CZNA-581 follow-up direct Publikovat/Odeslat ke schválení
     * action: create the draft exactly as a plain save would, then immediately call
     * the matching endpoint on the id it just got back. Only reachable on a CREATE_NEW
     * mount (see `handlePublishDirect`/`handleSubmitForReviewDirect` below), so it is
     * read only inside the `else` (create) branch.
     */
    /** CZNA-321 — publish a draft this mount just saved, for the `onCreated` hand-back. */
    var publishCreated = function (id) { return __awaiter(void 0, void 0, void 0, function () {
        var published;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, publishProposal(ky, id)];
                case 1:
                    published = _a.sent();
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    return [2 /*return*/, published];
            }
        });
    }); };
    /**
     * CZNA-1625 — "Načíst znovu" under an edit of this proposal that the server did not answer
     * in time. Reads the proposal (past the query cache: ProposalEdit keys this form on the
     * version, so an invalidation would remount it and wipe the edits) and compares versions:
     *
     *  - unchanged → nothing was written. The version read becomes the one the next save goes
     *    out on (`savedSnapshot`, as after any held-open save), and the edits stay.
     *  - moved → the write landed: what a successful save does next — the callout, then the
     *    detail. Except for a save that holds the form open (check findings) and one that a
     *    publish/submit/approve/reject was to FOLLOW: that next step never ran, so the form stays,
     *    now on the new version, for the cataloguer to repeat it. The back-link offers and
     *    needsReview rode on the response that never arrived.
     *  - unreadable → nobody knows yet.
     */
    var verifyTimedOutEdit = function (id, sentLock, options) { return __awaiter(void 0, void 0, void 0, function () {
        var fresh, _a, outcome;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, fetchProposalDetail(ky, id)];
                case 1:
                    fresh = _c.sent();
                    return [3 /*break*/, 3];
                case 2:
                    _a = _c.sent();
                    return [2 /*return*/, 'unverified'];
                case 3:
                    outcome = verificationFromVersions(sentLock, fresh === null || fresh === void 0 ? void 0 : fresh.optimisticLock);
                    if (outcome === 'unverified')
                        return [2 /*return*/, outcome];
                    setSavedSnapshot({ id: id, optimisticLock: fresh.optimisticLock });
                    if (outcome === 'notSaved')
                        return [2 /*return*/, outcome];
                    confirmSaved(fresh.authorityId, options.findings);
                    if (options.holdOpen || options.then || options.followUp) {
                        if (options.formData)
                            (_b = markSavedRef.current) === null || _b === void 0 ? void 0 : _b.call(markSavedRef, options.formData);
                        return [2 /*return*/, outcome];
                    }
                    queryClient.invalidateQueries(['proposal', id]);
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    setNeedsReviewItems([]);
                    if (onCreated) {
                        onCreated(fresh);
                    }
                    else {
                        history.push(returnDetailUrl);
                    }
                    return [2 /*return*/, outcome];
            }
        });
    }); };
    /**
     * CZNA-1625 — what a proposal write rejects with when nobody answered it in time. An edit
     * can be verified against the stored version; a create has no id to read, so it points at
     * the proposal list — in a new window, so the filled form stays for a fresh "Uložit" if the
     * proposal is not there.
     */
    var timedOutWriteError = function (edit) { return (edit
        ? saveOutcomeUnknownError(intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveTimeout' }), { kind: 'verify', verify: function () { return verifyTimedOutEdit(edit.id, edit.sentLock, edit.options); } })
        : saveOutcomeUnknownError(intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.createTimeout' }), {
            kind: 'checkList',
            href: PROPOSALS_LIST_HREF,
            linkLabelId: 'ui-czna-authorities.proposalForm.createTimeout.openList',
        })); };
    var performSave = function (proposalData, options) { return __awaiter(void 0, void 0, void 0, function () {
        var isEffectivelyEdit, writeAnswered, sentLock, saved, _a, _b, created, _c, _d, error_1, failure, errorInfo, messageId;
        var _e, _f, _g, _h, _j, _k;
        return __generator(this, function (_l) {
            switch (_l.label) {
                case 0:
                    isEffectivelyEdit = isEdit || savedSnapshot !== null;
                    writeAnswered = false;
                    sentLock = effectiveOptimisticLock;
                    _l.label = 1;
                case 1:
                    _l.trys.push([1, 27, , 30]);
                    if (!isEffectivelyEdit) return [3 /*break*/, 14];
                    return [4 /*yield*/, updateProposal(ky, effectiveProposalId, {
                            type: recordType,
                            optimisticLock: sentLock,
                            proposal_data: proposalData,
                        })];
                case 2:
                    saved = _l.sent();
                    writeAnswered = true;
                    // CZNA-785 — set BEFORE the hold-open branch below, not after it. The write has
                    // landed by this line; whether the form stays or navigates is a separate question
                    // and the answer must not decide whether the cataloguer is told.
                    confirmSaved(saved.authorityId, options === null || options === void 0 ? void 0 : options.findings);
                    // CZNA-763: unconditionally, before the branch. The row's version moved, so anything
                    // this mount does next has to use the new one — and "this mount does something next"
                    // is not only the hold-open case: the `onCreated` branch below hands control back to
                    // the picker with the form still mounted. Setting it on the navigate path too is
                    // harmless (the component unmounts) and makes "the snapshot holds the last version
                    // the server confirmed" an invariant rather than a per-branch accident.
                    setSavedSnapshot({ id: saved.id, optimisticLock: saved.optimisticLock });
                    (_e = options === null || options === void 0 ? void 0 : options.written) === null || _e === void 0 ? void 0 : _e.call(options);
                    return [4 /*yield*/, offerBackLinks(saved.backLinkOffers)];
                case 3:
                    _l.sent();
                    if ((options === null || options === void 0 ? void 0 : options.holdOpen) || (saved.needsReview && saved.needsReview.length > 0)) {
                        // Non-blocking — the save already succeeded. Hold the form open so what
                        // it reported (the needsReview banner, the check counts) stays in view
                        // instead of navigating away immediately; the user can still leave via
                        // Cancel at any time.
                        //
                        // Deliberately DOES NOT invalidate the ['proposal', id] query here:
                        // ProposalEdit renders `<ProposalForm key={`${id}-${optimisticLock}`}>`,
                        // so invalidating while this component stays mounted would trigger a
                        // background refetch that bumps `optimisticLock` and changes that
                        // `key` — remounting this form and wiping the outcome state we just
                        // set before the user ever sees it. The invalidation happens on the
                        // next save that actually navigates away (below), or is superseded
                        // by this response's own `saved` snapshot in the meantime.
                        setNeedsReviewItems((_f = saved.needsReview) !== null && _f !== void 0 ? _f : []);
                        // CZNA-1450 — the form stays, so what it now holds IS the saved draft. Without
                        // this the leave guard kept measuring against the values the form was opened
                        // with, and the cataloguer could not leave the draft she had just saved.
                        if (options === null || options === void 0 ? void 0 : options.formData)
                            (_g = markSavedRef.current) === null || _g === void 0 ? void 0 : _g.call(markSavedRef, options.formData);
                        return [2 /*return*/];
                    }
                    queryClient.invalidateQueries(['proposal', effectiveProposalId]);
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    setNeedsReviewItems([]);
                    if (!onCreated) return [3 /*break*/, 7];
                    // CZNA-321: the retry of a publishing child whose first publish failed after
                    // the draft landed — publish now, hand back the published proposal.
                    _a = onCreated;
                    if (!((options === null || options === void 0 ? void 0 : options.then) === 'publish')) return [3 /*break*/, 5];
                    return [4 /*yield*/, publishCreated(saved.id)];
                case 4:
                    _b = _l.sent();
                    return [3 /*break*/, 6];
                case 5:
                    _b = saved;
                    _l.label = 6;
                case 6:
                    // CZNA-321: the retry of a publishing child whose first publish failed after
                    // the draft landed — publish now, hand back the published proposal.
                    _a.apply(void 0, [_b]);
                    return [3 /*break*/, 13];
                case 7:
                    if (!(options === null || options === void 0 ? void 0 : options.then)) return [3 /*break*/, 12];
                    if (!(options.then === 'publish')) return [3 /*break*/, 9];
                    return [4 /*yield*/, publishProposal(ky, saved.id)];
                case 8:
                    _l.sent();
                    return [3 /*break*/, 11];
                case 9: return [4 /*yield*/, submitProposal(ky, saved.id)];
                case 10:
                    _l.sent();
                    _l.label = 11;
                case 11:
                    history.push(returnListUrl.split('?')[0]);
                    return [3 /*break*/, 13];
                case 12:
                    history.push(returnDetailUrl);
                    _l.label = 13;
                case 13: return [3 /*break*/, 26];
                case 14: return [4 /*yield*/, createProposal(ky, __assign(__assign({ type: recordType, proposalType: proposalType !== null && proposalType !== void 0 ? proposalType : 'CREATE_NEW' }, (proposalType === 'UPDATE_EXISTING' && targetRecordId
                        ? __assign({ targetRecordId: targetRecordId }, (targetRecordVersion != null ? { targetRecordVersion: targetRecordVersion } : {})) : {})), { proposal_data: proposalData }))];
                case 15:
                    created = _l.sent();
                    writeAnswered = true;
                    confirmSaved(created.authorityId, options === null || options === void 0 ? void 0 : options.findings);
                    // CZNA-581 follow-up — set BEFORE every branch below, including the plain
                    // navigate-away one. The draft has landed by this line regardless of what
                    // happens next, and `options?.then` (below) can still fail AFTER this
                    // point — without the id on hand first, a retry (or a second click before
                    // the form unmounts) would re-POST and create a duplicate proposal, the
                    // exact failure `savedSnapshot` exists to prevent for the needsReview and
                    // WARNING-confirm paths.
                    setSavedSnapshot({ id: created.id, optimisticLock: created.optimisticLock });
                    (_h = options === null || options === void 0 ? void 0 : options.written) === null || _h === void 0 ? void 0 : _h.call(options);
                    return [4 /*yield*/, offerBackLinks(created.backLinkOffers)];
                case 16:
                    _l.sent();
                    if ((options === null || options === void 0 ? void 0 : options.holdOpen) || (created.needsReview && created.needsReview.length > 0)) {
                        // Same rationale as the edit branch — stay put, show what the save reported.
                        // The embedded (onCreated) picker flow is unaffected: it always
                        // hands the full ProposalDetail (including needsReview) back to
                        // the caller, which may inspect it independently.
                        setNeedsReviewItems((_j = created.needsReview) !== null && _j !== void 0 ? _j : []);
                        // CZNA-1450 — same as the edit branch above.
                        if (options === null || options === void 0 ? void 0 : options.formData)
                            (_k = markSavedRef.current) === null || _k === void 0 ? void 0 : _k.call(markSavedRef, options.formData);
                        return [2 /*return*/];
                    }
                    if (!onCreated) return [3 /*break*/, 20];
                    _c = onCreated;
                    if (!((options === null || options === void 0 ? void 0 : options.then) === 'publish')) return [3 /*break*/, 18];
                    return [4 /*yield*/, publishCreated(created.id)];
                case 17:
                    _d = _l.sent();
                    return [3 /*break*/, 19];
                case 18:
                    _d = created;
                    _l.label = 19;
                case 19:
                    _c.apply(void 0, [_d]);
                    return [3 /*break*/, 26];
                case 20:
                    if (!(options === null || options === void 0 ? void 0 : options.then)) return [3 /*break*/, 25];
                    if (!(options.then === 'publish')) return [3 /*break*/, 22];
                    return [4 /*yield*/, publishProposal(ky, created.id)];
                case 21:
                    _l.sent();
                    return [3 /*break*/, 24];
                case 22: return [4 /*yield*/, submitProposal(ky, created.id)];
                case 23:
                    _l.sent();
                    _l.label = 24;
                case 24:
                    // Same destination `ProposalDetail`'s own onSuccess navigates to after
                    // either action: the proposal is no longer an actionable draft — DRAFT
                    // publish/submit is a one-way door — so there is nothing left to select
                    // in the list, only the bare list itself.
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    history.push(returnListUrl.split('?')[0]);
                    return [3 /*break*/, 26];
                case 25:
                    // CZNA-785 follow-up — land on the record that was just created, not on the
                    // bare list. This is the only save outcome with NO acknowledgement of any
                    // kind: check findings hold the form open with them counted in the header,
                    // needsReview holds it open with its own banner. The clean
                    // create closed the form and dropped the cataloguer into a list of hundreds
                    // with nothing naming what had just been written — "možná se to uložilo,
                    // možná ne, nevím" (NK, 28. 8. 2026, on the very scenario this ticket shipped).
                    //
                    // It was invisible until now because a clean create was effectively
                    // unreachable: every new draft carried NO_FIELD_001, NO_FIELD_008 and
                    // ALEPH_6101, so the warning modal always fired and WAS the acknowledgement.
                    // Removing those three rules removed the acknowledgement with them.
                    //
                    // The detail pane, not a toast: it survives the navigation (a Callout is
                    // mounted inside RecordForm and dies with it), it names the PROP-… number, and
                    // it is exactly where an EDIT save already lands — the asymmetry between the
                    // two branches had no reason behind it.
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    history.push("".concat(returnListUrl.split('?')[0], "/").concat(created.id));
                    _l.label = 26;
                case 26: return [3 /*break*/, 30];
                case 27:
                    error_1 = _l.sent();
                    // CZNA-1625: the write went unanswered — it may have landed. Say so, with the way to
                    // find out, instead of the connection advice that invites a second write.
                    if (!writeAnswered && isTimeoutError(error_1)) {
                        throw timedOutWriteError(isEffectivelyEdit
                            ? { id: effectiveProposalId, sentLock: sentLock, options: options !== null && options !== void 0 ? options : {} }
                            : null);
                    }
                    return [4 /*yield*/, parseValidationFailedError(error_1)];
                case 28:
                    failure = _l.sent();
                    if (failure) {
                        setValidationResult(failure.validationResult);
                        setSavedWithErrors(false);
                        setCheckStale(false);
                        setCheckPanelOpen(true);
                        // Throw a translated message so RecordForm's catch sets saveErrorMessage too.
                        throw new Error(intl.formatMessage({ id: 'ui-czna-authorities.validation.banner.serverRejected' }));
                    }
                    return [4 /*yield*/, getErrorInfo(error_1)];
                case 29:
                    errorInfo = _l.sent();
                    messageId = getProposalErrorMessageId(errorInfo);
                    throw new Error(intl.formatMessage({ id: messageId }));
                case 30: return [2 /*return*/];
            }
        });
    }); };
    /** The client-side validation of `proposalData` under `operation`. Throws when the endpoint fails. */
    var preflight = function (proposalData, operation) { return (validateRecord(ky, {
        record: proposalDataToMarc(proposalData),
        recordType: validationRecordType,
        operation: operation,
        // CZNA-221: for an UPDATE_EXISTING proposal the heading legitimately
        // matches the target record — exclude it so the duplicate check does not
        // warn about the very record being amended. CREATE_NEW has no self.
        excludeAuthorityId: formMode === 'update'
            ? (targetRecordAuthorityId !== null && targetRecordAuthorityId !== void 0 ? targetRecordAuthorityId : undefined)
            : undefined,
        // CZNA-313: when editing an existing draft, exclude this proposal from the
        // proposal-vs-proposal duplicate check so it does not warn about itself.
        //
        // CZNA-785 — `effectiveProposalId`, not `isEdit ? proposalId : undefined`. The old
        // expression was right only about the mount it was written for. On a CREATE mount
        // `isEdit` is false and `proposalId` is undefined, so nothing was excluded — and
        // that stops being harmless the moment the first save creates the draft: every
        // later save on the SAME mount is a PUT to a row that now exists, and the check
        // dutifully reported it as a duplicate of itself. Measured on the live stack:
        // PROP-001666 warning "stejné záhlaví už existuje — rozpracované návrhy:
        // PROP-001666"; re-run with its own id excluded, 0 errors and 0 warnings.
        //
        // The consequence was not just a spurious line. A warning then parked the save behind
        // a confirm modal (gone since CZNA-1518), so from the cataloguer's side the button
        // blinked and the click wrote nothing until they confirmed a warning about their own
        // record — "dal jsem uložit, tlačítko disabled, pak enabled a nic
        // se nestalo" (NK, 28. 8. 2026).
        //
        // `effectiveProposalId` is `savedSnapshot?.id ?? proposalId`, i.e. the id the row
        // actually has by now. This is the same correction `effectiveOptimisticLock` got
        // in CZNA-763, for the same reason and one expression to the left: after any save
        // that keeps the form mounted, the bare prop describes a state the row has moved
        // past.
        excludeProposalId: effectiveProposalId,
        // CZNA-785 — this payload IS the record about to be saved, so it should be judged
        // with the control fields the system supplies (008), the same ones the MARC preview
        // above already shows and the save then stamps into proposal_data. Without it a
        // brand-new proposal always failed NO_FIELD_008, naming a field no template
        // declares. Opt-in per request, not per route: the record-detail recheck and the
        // MARC checker call the same endpoint about records that already exist, and
        // deriving for them would report a stored record clean.
        // CZNA-1506 — it also stamps a picked 5XX link's $7 from the FK `proposalDataToMarc`
        // now carries on its $a, as the publish does, so ALEPH_6116 no longer warns a change
        // proposal about a $7 the published record will have.
        applyDerivedControlFields: true,
        // CZNA-1619 — `proposalDataToMarc` carries the FK of every row the pickers linked, so a
        // 368/372/374 value without one is unlinked and the check warns about it.
        carriesLinks: true,
    }).then(completeVerdict)); };
    /** Every check starts from nothing — the last outcome is not this one's. */
    var resetValidationOutcome = function () {
        setSavedWithErrors(false);
        setSavedWithWarnings(false);
        setConfirmWarnings(null);
        setBlockedAction(null);
        setCheckStale(false);
        setCheckPanelOpen(false);
        // The issue list too: kept, it stood under whatever heading the reset left, and the
        // tab counts went on reporting the previous attempt while this one was in flight.
        setValidationResult(null);
    };
    /**
     * Run the validation preflight, then either:
     *  - save and hold the form open with the findings counted (BLOCKING errors — CZNA-582:
     *    a save is never blocked, only reported; WARNINGS the same way, CZNA-1518), or
     *  - perform the save (no issues).
     *
     * `then` (CZNA-581 follow-up) makes this ALSO back the direct Publikovat/Odeslat ke
     * schválení buttons: same preflight, only the final write differs (`performSave`'s own
     * `options.then`). They part on issues (CZNA-1518): a blocking error writes nothing,
     * and warnings no longer stop it — the confirm dialog before this call already listed
     * them, and the "Ano" given there is the answer.
     */
    var runSave = function (formData, then, written) { return __awaiter(void 0, void 0, void 0, function () {
        var proposalData, operation, result, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    // CZNA-764: a new attempt starts with a clean slate — a message left over from the
                    // previous one is its own way of misreporting this one's outcome.
                    resetValidationOutcome();
                    proposalData = genericFormToProposalData(config, formData);
                    operation = (function () {
                        // CZNA-552 — EVERY review stage validates under the full review rules. Driven off
                        // the shared list rather than status literals: a future third pending state would
                        // otherwise fall through to UPDATE_DRAFT and loosen validation for a step that
                        // publishes, which is the one thing this branch must never do.
                        //
                        // CZNA-785 — SUBMIT_FOR_REVIEW, not PUBLISH, and that is not a loosening. The two
                        // events carry the same severity profile (both BLOCKING, changeset 120 even copies
                        // each rule's PUBLISH override across); what separates them is membership, and the
                        // two rules PUBLISH has on top are NO_FIELD_001 and ALEPH_6101 — the control number
                        // and the heading $7, both assigned in the saving transaction of a published record.
                        // What this preflight is looking at is a PROPOSAL, which by design holds neither, so
                        // under PUBLISH it reported two blocking errors on every pending proposal that
                        // nobody could clear from the form. The gate this preflight is warning about is
                        // ProposalServiceImpl#submitProposal, which validates under exactly this event; the
                        // publish gate that does use PUBLISH runs on a Record, whose 001 comes from
                        // export_id (CZNA-691), so it is a different question about a different object.
                        if (PENDING_PROPOSAL_STATUSES.includes(proposalStatus)) {
                            return 'SUBMIT_FOR_REVIEW';
                        }
                        // CZNA-581 follow-up — a direct Publikovat/Odeslat click IS the submit-for-review
                        // event, not a draft save that happens to be followed by one: judging it under
                        // CREATE_DRAFT's looser rules could clear the preflight (no errors reported) and
                        // only then discover, from the server's OWN submit/publish gate, that
                        // the record actually has blocking errors — after the draft was already created.
                        // Same rules the follow-up call itself enforces, checked before committing to it.
                        if (then) {
                            return 'SUBMIT_FOR_REVIEW';
                        }
                        return isEdit ? 'UPDATE_DRAFT' : 'CREATE_DRAFT';
                    })();
                    result = null;
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, preflight(proposalData, operation)];
                case 2:
                    result = _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    // CZNA-1518 — Publikovat/Odeslat does not go ahead unchecked. Falling through
                    // created the draft first and let the server's gate refuse the publish after it,
                    // which is exactly "a click that saved a koncept instead of what was asked".
                    // Nothing is written; the cataloguer can try again.
                    if (then) {
                        setValidationResult(null);
                        throw new Error(intl.formatMessage({ id: 'ui-czna-authorities.validation.check.unavailable' }));
                    }
                    // Preflight failed — fall through to save anyway. The backend will
                    // re-validate (defense-in-depth) and reject with 422 if needed; we
                    // don't want a flaky validation endpoint to block legitimate saves.
                    result = null;
                    return [3 /*break*/, 4];
                case 4:
                    setValidationResult(result);
                    if (result && result.errorCount > 0 && then) {
                        // CZNA-1518 — Publikovat/Odeslat with a blocking error writes NOTHING. It used to
                        // save the draft and report "Koncept je uložený" (CZNA-581 follow-up): the click
                        // did a third thing nobody asked for, and the cataloguer learned it had not
                        // published only after the fact. The form stays open with everything typed into
                        // it; "Uložit" is still there for whoever wants the draft.
                        setBlockedAction(then);
                        setCheckPanelOpen(true);
                        return [2 /*return*/];
                    }
                    if (!(result && result.errorCount > 0)) return [3 /*break*/, 6];
                    // CZNA-582 — errors DO NOT block a save. An unfinished draft fails the mandatory-
                    // field rules by definition, so gating "Uložit" here made drafts unsaveable and
                    // took the whole point of a draft with them. Nothing of a draft is public, so
                    // there is nothing for the gate to protect; the gate lives on the two buttons that
                    // do make it public — "odeslat ke schválení" (ProposalServiceImpl#submitProposal)
                    // and "publikovat" (#publishProposal), both server-side and both under SUBMIT_FOR_REVIEW
                    // rules (CZNA-785).
                    //
                    // Save, then hold the form open with the counts up instead of navigating away —
                    // "proběhne, zobrazí se, neblokuje". Navigating would save silently and the
                    // cataloguer would never learn what still needs fixing before they can submit.
                    // Same mechanism CZNA-411 uses for needsReview, for the same reason.
                    setSavedWithErrors(true);
                    return [4 /*yield*/, performSave(proposalData, {
                            holdOpen: true,
                            formData: formData,
                            written: written,
                            findings: intl.formatMessage({ id: 'ui-czna-authorities.validation.callout.savedWithErrors' }, { count: result.errorCount }),
                        })];
                case 5:
                    _b.sent();
                    return [2 /*return*/];
                case 6:
                    if (!(result && result.warningCount > 0 && !then)) return [3 /*break*/, 8];
                    // CZNA-1518 — warnings no longer ask "Uložit přesto?". They are weaker than errors,
                    // which never asked (CZNA-582), and a draft is visible to nobody but its author.
                    // Same outcome as errors: the draft is saved and the form stays open with the counts.
                    setSavedWithWarnings(true);
                    return [4 /*yield*/, performSave(proposalData, {
                            holdOpen: true,
                            formData: formData,
                            written: written,
                            findings: intl.formatMessage({ id: 'ui-czna-authorities.validation.callout.savedWithWarnings' }, { count: result.warningCount }),
                        })];
                case 7:
                    _b.sent();
                    return [2 /*return*/];
                case 8: 
                // Clean — proceed with the save (and the follow-up action, if any).
                return [4 /*yield*/, performSave(proposalData, { then: then, formData: formData, written: written })];
                case 9:
                    // Clean — proceed with the save (and the follow-up action, if any).
                    _b.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    // CZNA-1508 — see `performSave`'s `written`.
    var handleSave = function (formData, signals) { return (runSave(formData, undefined, signals === null || signals === void 0 ? void 0 : signals.written)); };
    /** @see RecordForm's onPublish */
    var handlePublishDirect = function (formData) { return runSave(formData, 'publish'); };
    /** @see RecordForm's onSubmitForReview */
    var handleSubmitForReviewDirect = function (formData) { return runSave(formData, 'submit'); };
    /**
     * CZNA-1518 — Zkontrolovat, and the check Publikovat/Odeslat runs before its confirm
     * dialog. Writes nothing. Judged under SUBMIT_FOR_REVIEW, the event the server's own
     * submit and publish gates use (ProposalServiceImpl#submitProposal/#publishProposal),
     * so "bez chyb" here means those gates let it through.
     *
     * @see RecordForm's onCheck
     */
    var handleCheck = function (formData_1) {
        var args_1 = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args_1[_i - 1] = arguments[_i];
        }
        return __awaiter(void 0, __spreadArray([formData_1], args_1, true), void 0, function (formData, intent) {
            var result, _a;
            if (intent === void 0) { intent = 'check'; }
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        resetValidationOutcome();
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, preflight(genericFormToProposalData(config, formData), 'SUBMIT_FOR_REVIEW')];
                    case 2:
                        result = _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        _a = _b.sent();
                        setValidationResult(null);
                        throw new Error(intl.formatMessage({ id: 'ui-czna-authorities.validation.check.unavailable' }));
                    case 4:
                        setValidationResult(result);
                        if (result.errorCount > 0) {
                            setBlockedAction(intent);
                            setCheckPanelOpen(true);
                            return [2 /*return*/, 'errors'];
                        }
                        // CZNA-1518 — before a publish/submit the warnings belong in the confirm dialog: the
                        // cataloguer decides with them in front of them, not after the "Ano".
                        if (intent !== 'check' && result.warningCount > 0)
                            setConfirmWarnings(result.warnings);
                        // Before a publish/submit the confirm dialog that follows is the answer; the panel's
                        // "bez chyb" is for the button that asked only that.
                        if (intent === 'check')
                            setCheckPanelOpen(true);
                        return [2 /*return*/, 'clean'];
                }
            });
        });
    };
    var handleSaveAndApprove = function (formData) { return __awaiter(void 0, void 0, void 0, function () {
        var proposalData, sentLock, saved, error_2, errorInfo, messageId, error_3, approveFailure, approveErrorInfo;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    // CZNA-1518: a check's verdict may not outlive the save after it.
                    setBlockedAction(null);
                    proposalData = genericFormToProposalData(config, formData);
                    sentLock = effectiveOptimisticLock;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 5]);
                    return [4 /*yield*/, updateProposal(ky, effectiveProposalId, {
                            type: recordType,
                            optimisticLock: sentLock,
                            proposal_data: proposalData,
                        })];
                case 2:
                    saved = _a.sent();
                    // CZNA-763: reading the tracked version is only half of it — the row moved, so record
                    // where it moved to. Step 2 below can fail routinely (the CZNA-582 publish gate answers
                    // 422; scope answers 403 when approval belongs to NK), and the whole point of those
                    // branches is that the user stays on this form and retries. Without this line the retry
                    // re-sends the version from the mount and 409s on this very save. (The other 403 this
                    // used to name — the reviewer-is-submitter guard — is gone as of CZNA-781; the reason
                    // for the line is not.)
                    setSavedSnapshot({ id: saved.id, optimisticLock: saved.optimisticLock });
                    return [3 /*break*/, 5];
                case 3:
                    error_2 = _a.sent();
                    // CZNA-1625: the save leg went unanswered. Verified like any edit; the second leg never
                    // ran, so a save that did land holds the form open for the cataloguer to repeat it.
                    if (isTimeoutError(error_2)) {
                        throw timedOutWriteError({
                            id: effectiveProposalId,
                            sentLock: sentLock,
                            options: { followUp: true, formData: formData },
                        });
                    }
                    return [4 /*yield*/, getErrorInfo(error_2)];
                case 4:
                    errorInfo = _a.sent();
                    messageId = getProposalErrorMessageId(errorInfo);
                    throw new Error(intl.formatMessage({ id: messageId }));
                case 5:
                    _a.trys.push([5, 7, , 10]);
                    // Step 2: Approve the proposal
                    return [4 /*yield*/, approveProposal(ky, effectiveProposalId)];
                case 6:
                    // Step 2: Approve the proposal
                    _a.sent();
                    return [3 /*break*/, 10];
                case 7:
                    error_3 = _a.sent();
                    // Save succeeded but approve failed — inform user.
                    //
                    // CZNA-608 — a 403 gets its own message. The generic one tells the user to retry
                    // from the proposal detail, and for a permission refusal that retry fails
                    // identically, forever.
                    //
                    // CZNA-781 removed the loudest cause this branch used to have: Save+Approve on your
                    // own submission. At NK the subject cataloger is also the local supervisor
                    // (CZNA-552), so that path hit the reviewer-is-the-submitter guard every time; the
                    // backend no longer asks who submitted the proposal, so it now succeeds.
                    //
                    // What remains is scope, and the message must still say WHAT was refused, never WHY,
                    // while this branch has only a status to go on: this route sets reviewMode from the
                    // status alone (ProposalEdit), so a LOCAL supervisor is offered Save+Approve on a
                    // PENDING_NATIONAL_REVIEW proposal and is refused because approval is NK's
                    // (AuthorizationScope.nationalOnly). Naming a cause here would state a guess as a
                    // fact, which is the failure the CZNA-582 work exists to remove.
                    //
                    // If a cause is ever wanted, the backend has to send a machine-readable one first.
                    // Do NOT reach for the free text it sends today: handleForbidden emits only
                    // {"error": <message>}, so getErrorInfo().message is a developer-worded English
                    // string ("You do not have permission to perform this action") and putting it in
                    // front of a Czech cataloguer is a different way of being unhelpful. ErrorInfo
                    // already carries `type`; what is missing is a stable code on this response.
                    queryClient.invalidateQueries(['proposal', effectiveProposalId]);
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    return [4 /*yield*/, parseValidationFailedError(error_3)];
                case 8:
                    approveFailure = _a.sent();
                    if (approveFailure) {
                        setValidationResult(approveFailure.validationResult);
                        setSavedWithErrors(false);
                        setCheckStale(false);
                        setCheckPanelOpen(true);
                        throw new Error(intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveAndApprove.validationRejected' }));
                    }
                    return [4 /*yield*/, getErrorInfo(error_3)];
                case 9:
                    approveErrorInfo = _a.sent();
                    throw new Error(intl.formatMessage({
                        id: secondLegMessageIdForKind('approve', getErrorKind(approveErrorInfo)),
                    }));
                case 10:
                    queryClient.invalidateQueries(['proposal', effectiveProposalId]);
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    history.push(returnListUrl);
                    return [2 /*return*/];
            }
        });
    }); };
    /**
     * Save+Reject chain (TASK-080): persist edits, then reject in one user action.
     * Mirrors {@link handleSaveAndApprove} — first leg failure aborts; second leg
     * failure surfaces a parallel translated message and leaves proposal saved
     * but still PENDING_REVIEW.
     */
    var handleSaveAndReject = function (formData, rejection) { return __awaiter(void 0, void 0, void 0, function () {
        var proposalData, sentLock, saved, error_4, errorInfo, messageId, error_5, rejectErrorInfo;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    // CZNA-1518: a check's verdict may not outlive the save after it.
                    setBlockedAction(null);
                    proposalData = genericFormToProposalData(config, formData);
                    sentLock = effectiveOptimisticLock;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 5]);
                    return [4 /*yield*/, updateProposal(ky, effectiveProposalId, {
                            type: recordType,
                            optimisticLock: sentLock,
                            proposal_data: proposalData,
                        })];
                case 2:
                    saved = _a.sent();
                    // CZNA-763: reading the tracked version is only half of it — the row moved, so record
                    // where it moved to. Step 2 below can fail routinely (the CZNA-582 publish gate answers
                    // 422; scope answers 403 when approval belongs to NK), and the whole point of those
                    // branches is that the user stays on this form and retries. Without this line the retry
                    // re-sends the version from the mount and 409s on this very save. (The other 403 this
                    // used to name — the reviewer-is-submitter guard — is gone as of CZNA-781; the reason
                    // for the line is not.)
                    setSavedSnapshot({ id: saved.id, optimisticLock: saved.optimisticLock });
                    return [3 /*break*/, 5];
                case 3:
                    error_4 = _a.sent();
                    // CZNA-1625: the save leg went unanswered. Verified like any edit; the second leg never
                    // ran, so a save that did land holds the form open for the cataloguer to repeat it.
                    if (isTimeoutError(error_4)) {
                        throw timedOutWriteError({
                            id: effectiveProposalId,
                            sentLock: sentLock,
                            options: { followUp: true, formData: formData },
                        });
                    }
                    return [4 /*yield*/, getErrorInfo(error_4)];
                case 4:
                    errorInfo = _a.sent();
                    messageId = getProposalErrorMessageId(errorInfo);
                    throw new Error(intl.formatMessage({ id: messageId }));
                case 5:
                    _a.trys.push([5, 7, , 9]);
                    // Step 2: Reject the proposal
                    return [4 /*yield*/, rejectProposal(ky, effectiveProposalId, {
                            rejectionReason: rejection.rejectionReason,
                            reviewComment: rejection.reviewComment,
                        })];
                case 6:
                    // Step 2: Reject the proposal
                    _a.sent();
                    return [3 /*break*/, 9];
                case 7:
                    error_5 = _a.sent();
                    // Save succeeded but reject failed — keep saved data, ask user to retry reject.
                    // CZNA-608/609 — 403 and 401 get their own message; see handleSaveAndApprove and
                    // secondLegMessageId for why "retry from the detail" is the wrong advice for them.
                    queryClient.invalidateQueries(['proposal', effectiveProposalId]);
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    return [4 /*yield*/, getErrorInfo(error_5)];
                case 8:
                    rejectErrorInfo = _a.sent();
                    throw new Error(intl.formatMessage({
                        id: secondLegMessageIdForKind('reject', getErrorKind(rejectErrorInfo)),
                    }));
                case 9:
                    queryClient.invalidateQueries(['proposal', effectiveProposalId]);
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    history.push(returnListUrl);
                    return [2 /*return*/];
            }
        });
    }); };
    // TASK-159: the check's findings, from the client-side preflight or from a 422 server
    // response. Inline per-field errors are not yet propagated into the dynamic field
    // renderer (Phase 1 scope) — the issue list with "jump to field" links stands in for
    // them; since CZNA-1518 it lives in the check panel, opened from the pane header.
    //
    // CZNA-582: "blocking" now names the ISSUES, not the outcome — a draft save goes
    // through with them and reports them (savedWithErrors), while submit and publish are
    // refused by the server.
    var hasBlockingErrors = !!validationResult && validationResult.errorCount > 0;
    var hasWarnings = !!validationResult && validationResult.warningCount > 0;
    // CZNA-582 / CZNA-1518: the same issue list, several different pieces of news. The panel
    // says which one, and what it means for the next step; a bare count needs no gloss.
    var panelNews = (function () {
        if (!validationResult)
            return null;
        var errorCount = validationResult.errorCount, warningCount = validationResult.warningCount;
        if (errorCount > 0) {
            if (savedWithErrors) {
                return {
                    tone: 'error',
                    id: 'ui-czna-authorities.validation.banner.savedWithErrors',
                    noteId: 'ui-czna-authorities.validation.banner.savedWithErrors.note',
                    count: errorCount,
                };
            }
            if (blockedAction) {
                return {
                    tone: 'error',
                    id: "ui-czna-authorities.validation.banner.".concat(blockedAction, "Blocked"),
                    noteId: "ui-czna-authorities.validation.banner.".concat(blockedAction, "Blocked.note"),
                    count: errorCount,
                };
            }
            return { tone: 'error', id: 'ui-czna-authorities.validation.banner.errorCount', count: errorCount };
        }
        if (warningCount > 0) {
            if (savedWithWarnings) {
                return {
                    tone: 'warning',
                    id: 'ui-czna-authorities.validation.banner.savedWithWarnings',
                    noteId: 'ui-czna-authorities.validation.banner.savedWithWarnings.note',
                    count: warningCount,
                };
            }
            return {
                tone: 'warning',
                id: 'ui-czna-authorities.validation.check.passedWithWarnings',
                noteId: 'ui-czna-authorities.validation.check.passedWithWarnings.note',
                count: warningCount,
            };
        }
        return { tone: 'ok', id: 'ui-czna-authorities.validation.check.passed', count: 0 };
    })();
    // The needs-review banner sits at the top of RecordForm's scrolling content, while the
    // buttons that produce it sit in its footer under a form routinely taller than the
    // viewport — bring it into view when it gets new news (CZNA-764). `scrollIntoView` is
    // guarded because jsdom does not implement it. The check needs no such scroll: its
    // counts sit in the pane header and its panel over the form, both always in view.
    var needsReviewBannerRef = useRef(null);
    useEffect(function () {
        var _a, _b;
        if (needsReviewItems.length === 0)
            return;
        (_b = (_a = needsReviewBannerRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, { block: 'nearest' });
    }, [needsReviewItems]);
    // CZNA-581 follow-up — the direct Publikovat/Odeslat ke schválení quick action.
    // Scoped to the top-level CREATE route only:
    //  - `!isEdit` — a proposal already on the board has its own action on
    //    `ProposalDetail`, which additionally gates on authorship (`isAuthor`); this
    //    form has no such check to make and must not reimplement one badly.
    //  - `!onCreated` — the picker's embedded propose-GA flow hands the fresh draft
    //    straight back to the parent form (a linking id, not an independent
    //    publish/submit decision); offering either button there would let a
    //    sub-proposal skip the very review its parent may still need.
    //    CZNA-321: except `publishOnCreate` — a type that publishes immediately has no
    //    review to skip, and its child is offered only where the backend said it would
    //    publish, so "Publikovat" is the child's ONLY action (`hideSaveButton`).
    // CZNA-767 (blind review) — this route now ALSO reaches this mount with
    // `proposalType="UPDATE_EXISTING"` (`ProposalCreateFromRecord`), so the comment
    // that used to stand here ("every route reaching this mount in create mode is
    // CREATE_NEW by construction") is no longer true. `publishProposal` on the backend
    // refuses any UPDATE_EXISTING outright ("an UPDATE_EXISTING proposal always goes
    // through review — submit it instead"); `submitProposal`, conversely, exempts
    // UPDATE_EXISTING from the publishesImmediately check entirely — an amendment
    // always goes through review regardless of authority type. So for an amendment the
    // direct action is always "submit", never "publish", independent of the type's own
    // publishesImmediately-ness.
    // CZNA-321: a blocking finding on a hidden tab reveals that tab.
    var shownTabIds = useMemo(function () { return withTabsCarryingErrors(config, visibleTabIds, validationResult === null || validationResult === void 0 ? void 0 : validationResult.errors); }, [config, visibleTabIds, validationResult]);
    // CZNA-1451: the check panel names a field the way the form does — its name and the tab
    // it sits on — and offers the jump only for a field the form actually shows.
    var describeLocation = describeFieldLocation(shownTabs(config, shownTabIds), intl);
    // CZNA-1518 — a jump closes the panel: the field it leads to may sit under it.
    var jumpToField = function (location) {
        var _a;
        setCheckPanelOpen(false);
        (_a = jumpToFieldRef.current) === null || _a === void 0 ? void 0 : _a.call(jumpToFieldRef, location);
    };
    var offerDirectAction = !isEdit && !onCreated;
    var isAmendment = (proposalType !== null && proposalType !== void 0 ? proposalType : ProposalType.CREATE_NEW) === ProposalType.UPDATE_EXISTING;
    // CZNA-1432 — the review footer's help says what each decision does for THIS proposal.
    // `beforeNk`: a two-stage proposal still in local review, where "Uložit a schválit" saves but
    // the approval is refused — only NK approves it (ApprovalWorkflow.statusAwaitingFinalApproval).
    // CZNA-1526: every amendment is two-stage, whatever the type.
    var reviewStage = (function () {
        if (proposalStatus === ProposalStatus.PENDING_NATIONAL_REVIEW)
            return 'national';
        return isTwoStageApproval(recordType, proposalType) ? 'beforeNk' : 'local';
    })();
    var reviewHelpValues = {
        kind: isAmendment ? 'update' : 'create',
        stage: reviewStage,
    };
    var canPublishDirectly = (offerDirectAction || (!isEdit && !!publishOnCreate))
        && !isAmendment && publishesImmediately(recordType);
    var canSubmitForReviewDirectly = offerDirectAction
        && (isAmendment || !publishesImmediately(recordType));
    // Rendered by RecordForm INSIDE its pane (its `banners` prop). As siblings above the root
    // Paneset they added their height to the pane's 100 % and pushed the save footer below
    // the viewport.
    var banners = (React.createElement(React.Fragment, null,
        notice && React.createElement("div", { style: { marginBottom: '16px' } }, notice),
        needsReviewItems.length > 0 && (React.createElement("div", { ref: needsReviewBannerRef, "data-test-needs-review-banner": true, style: { marginBottom: '16px' } },
            React.createElement(MessageBanner, { type: "warning", dismissible: true, onExited: function () { return setNeedsReviewItems([]); } },
                React.createElement("strong", null,
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.needsReview.heading", values: { count: needsReviewItems.length } })),
                React.createElement("ul", { style: { marginTop: '8px', marginBottom: 0, paddingLeft: '20px' } }, needsReviewItems.map(function (item, index) {
                    var _a, _b, _c;
                    return (React.createElement("li", { key: "".concat(item.field, "-").concat(item.subfield, "-").concat((_a = item.componentType) !== null && _a !== void 0 ? _a : 'na', "-").concat(index) },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.needsReview.reason.".concat(item.reason), values: {
                                field: item.field,
                                subfield: item.subfield,
                                // CZNA-1428: same naming as the MARC preview (MarcDataDisplay) — a type
                                // without a translation key falls back to its code, never to the message id.
                                componentType: item.componentType
                                    ? intl.formatMessage({
                                        id: "ui-czna-authorities.componentType.".concat(item.componentType),
                                        defaultMessage: item.componentType,
                                    })
                                    : intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.needsReview.unknownComponent' }),
                                clientValue: (_b = item.clientValue) !== null && _b !== void 0 ? _b : intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.needsReview.emptyValue' }),
                                recomputedValue: (_c = item.recomputedValue) !== null && _c !== void 0 ? _c : intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.needsReview.emptyValue' }),
                            } })));
                })))))));
    // CZNA-1518 — the counts that open the panel. Nothing before the first check: the form
    // is not checked while it is filled in, so there is nothing to count yet.
    var checkPanelId = 'proposal-form-check-panel';
    var checkBadge = validationResult ? (React.createElement(CheckBadge, { ref: checkBadgeRef, errorCount: validationResult.errorCount, warningCount: validationResult.warningCount, stale: checkStale, expanded: checkPanelOpen, controls: checkPanelId, onClick: function () {
            var _a;
            // Counts that describe values since changed are not worth a look: check again,
            // and the panel opens on the fresh verdict.
            if (checkStale) {
                (_a = runCheckRef.current) === null || _a === void 0 ? void 0 : _a.call(runCheckRef);
                return;
            }
            setCheckPanelOpen(function (open) { return !open; });
        } })) : undefined;
    var checkPanel = validationResult && panelNews ? (React.createElement(CheckPanel, { id: checkPanelId, open: checkPanelOpen, onClose: function (restoreFocus) {
            var _a;
            setCheckPanelOpen(false);
            if (restoreFocus)
                (_a = checkBadgeRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }, opener: checkBadgeRef, tone: panelNews.tone, heading: React.createElement(FormattedMessage, { id: panelNews.id, values: { count: panelNews.count } }), note: panelNews.noteId && (React.createElement(FormattedMessage, { id: panelNews.noteId, values: { count: panelNews.count } })) },
        hasBlockingErrors && (React.createElement(ValidationIssueGroups, { issues: validationResult.errors, describeLocation: describeLocation, onLocationClick: jumpToField })),
        hasBlockingErrors && hasWarnings && (React.createElement("details", { "data-test-check-panel-warnings": true, style: { marginTop: '6px' } },
            React.createElement("summary", { style: { cursor: 'pointer', color: '#8a5d00', padding: '4px 0' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.banner.alsoWarnings", values: { count: validationResult.warningCount } })),
            React.createElement(ValidationIssueGroups, { issues: validationResult.warnings, describeLocation: describeLocation, onLocationClick: jumpToField }))),
        !hasBlockingErrors && hasWarnings && (React.createElement(ValidationIssueGroups, { issues: validationResult.warnings, describeLocation: describeLocation, onLocationClick: jumpToField })))) : undefined;
    // CZNA-1518 — shown inside the Publikovat/Odeslat confirm. No jump links: the modal hands
    // focus back to the button as it closes, which would undo the jump (same as WarningModal).
    var confirmNotice = confirmWarnings && confirmWarnings.length > 0 ? (React.createElement("div", { "data-test-confirm-warnings": true, style: { marginTop: '12px' } },
        React.createElement("strong", null,
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.confirm.warnings", values: { count: confirmWarnings.length } })),
        React.createElement("div", { style: { marginTop: '4px' } },
            React.createElement(ValidationIssueGroups, { issues: confirmWarnings, describeLocation: describeLocation })))) : undefined;
    return (React.createElement(React.Fragment, null,
        React.createElement(RecordForm, { config: config, recordType: recordType, initialData: convertedInitialData, banners: banners, isEdit: isEdit, mode: formMode, 
            // CZNA-977: these two props look interchangeable and are NOT. The next two lines
            // used to pass the same value; only the FIRST may switch to the control number.
            //   - targetRecordAuthorityIdDisplay is field 001, whose value IS export_id;
            //   - excludeAuthorityId excludes the record from its own namesake hint
            //     (CZNA-1034) and must match what the record SEARCH returns, i.e. authority_id.
            // Switching the second along with the first silently breaks the hint. Guarded by
            // ProposalForm.test.tsx ("keeps sending the authority id to excludeAuthorityId").
            targetRecordAuthorityIdDisplay: formMode === 'update'
                ? recordDisplayId({
                    type: recordType,
                    exportId: targetRecordExportId,
                    authorityId: targetRecordAuthorityId,
                })
                : null, excludeAuthorityId: formMode === 'update' ? targetRecordAuthorityId !== null && targetRecordAuthorityId !== void 0 ? targetRecordAuthorityId : undefined : undefined, excludeProposalId: proposalId !== null && proposalId !== void 0 ? proposalId : undefined, previewControlFields: previewControlFields, previewRecordType: validationRecordType, metadata: metadata, onSave: handleSave, onSaveAndApprove: reviewMode && canApprove ? handleSaveAndApprove : undefined, reviewHelpValues: reviewMode ? reviewHelpValues : undefined, onPublish: canPublishDirectly ? handlePublishDirect : undefined, onSubmitForReview: canSubmitForReviewDirectly ? handleSubmitForReviewDirect : undefined, onCheck: handleCheck, confirmNotice: confirmNotice, onCheckStale: function () { return setCheckStale(true); }, runCheckRef: runCheckRef, headerActions: checkBadge, checkPanel: checkPanel, onSaveAndReject: reviewMode && proposalId ? handleSaveAndReject : undefined, onCancel: isEdit ? function () { return history.push(returnDetailUrl); } : onCancel, 
            // TASK-080: in review mode the plain "Uložit" button is now visible alongside
            // "Uložit a schválit" and "Uložit a zamítnout" — hideSaveButton stays false.
            // CZNA-321: the minimal create's only action is Publikovat.
            hideSaveButton: canPublishDirectly && !!publishOnCreate, extraFooterActions: reviewMode && proposalId ? function (ctx) { return (React.createElement(RejectRequestChangesButtons, { id: proposalId, successReturnUrl: returnListUrl, isDirty: ctx.isDirty, busy: ctx.isSaving, onSaveAndReject: ctx.onSaveAndReject, helpValues: reviewHelpValues })); } : undefined, saveButtonLabelKey: isEdit ? undefined : 'ui-czna-authorities.proposalForm.saveDraft', paneId: "proposalFormPane", 
            // CZNA-197 (SF-1): thread `embedded` so the in-tab modal flow keeps
            // working. Written against the pre-CZNA-487 split, where this mattered
            // only "once GEOGRAPHIC migrates" off LegacyForm; LegacyForm is gone and
            // RecordForm now hosts every type, so this carries the modal flow whole.
            embedded: embedded, visibleTabIds: shownTabIds, 
            // CZNA-197: let the host Modal's single X route through RecordForm's
            // unsaved-changes guard instead of a bare close.
            requestCloseRef: requestCloseRef, markSavedRef: markSavedRef, jumpToFieldRef: jumpToFieldRef, validationIssues: validationResult
                ? __spreadArray(__spreadArray([], validationResult.errors, true), validationResult.warnings, true) : undefined, validationIssuesStale: checkStale, 
            // CZNA-1450 — once the proposal exists (an edit, or a create this mount has already
            // saved), leaving loses only what changed since the last save; the default text
            // ("…návrh se neuloží ani do Konceptů") is true only of a proposal never saved.
            leaveModalMessageKey: isEdit || savedSnapshot
                ? 'ui-czna-authorities.proposalForm.leaveModal.messageSaved'
                : undefined, 
            // CZNA-241: propose-GA-from-picker — where to return after the GA create
            // route, and the one-shot restore token when this is the return mount.
            returnTo: proposeReturnTo, restoreKey: restoreKey }),
        React.createElement(Callout, { ref: calloutRef }),
        backLinkOfferModal));
};
/**
 * Chrome shared by the two non-form states below (loading, unavailable).
 *
 * <p>Mirrors the two render paths {@link RecordForm} has: inside the picker's
 * in-tab Modal (`embedded`) NO Paneset/Pane may be rendered — a root Paneset
 * collapses to zero height in a Modal body and a non-root one registers a
 * phantom pane in the PARENT editor's PanesetContext, which is the CZNA-197
 * layout regression. Everywhere else it is the ordinary route Pane.
 */
var FormShell = function (_a) {
    var recordType = _a.recordType, onCancel = _a.onCancel, embedded = _a.embedded, footer = _a.footer, children = _a.children;
    var intl = useIntl();
    if (embedded) {
        return (React.createElement("div", { style: { display: 'flex', flexDirection: 'column', height: '70vh', maxHeight: '70vh' } },
            React.createElement("div", { style: { flex: '1 1 auto', overflow: 'auto', padding: '0 16px' } }, children),
            footer && (React.createElement("div", { style: { flex: '0 0 auto', borderTop: '1px solid #e0e0e0' } }, footer))));
    }
    return (React.createElement(Paneset, { isRoot: true },
        React.createElement(Pane, { id: "proposalFormPane", defaultWidth: "fill", paneTitle: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.title' }), paneSub: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(recordType) }), dismissible: true, onClose: onCancel, footer: footer }, children)));
};
/**
 * Loading skeleton shown while the form config is being fetched for the
 * first time. The window is typically very short (≤300 ms on a cold cache,
 * instant on stale-while-revalidate). TASK-178.
 */
var FormConfigSkeleton = function (_a) {
    var recordType = _a.recordType, onCancel = _a.onCancel, embedded = _a.embedded;
    return (React.createElement(FormShell, { recordType: recordType, onCancel: onCancel, embedded: embedded },
        React.createElement("div", { "data-testid": "form-config-loading", style: { padding: '32px', textAlign: 'center' } },
            React.createElement(Loading, { size: "large" }))));
};
/**
 * Terminal state for "this type's form config could not be resolved" — what
 * replaced `LegacyForm` in CZNA-487.
 *
 * <p><b>Why an error and not a fallback form.</b> `LegacyForm` rendered a fixed
 * set of PERSONAL/GEOGRAPHIC-shaped tabs (plus two "coming soon" placeholders)
 * for ANY type that reached it. That is a different record shape from the one
 * the type is actually catalogued in, and it saved through a different encoder
 * (`formToProposalData`), so the silent fallback did not degrade the form — it
 * produced the wrong one, with no signal anywhere except a `console.warn`.
 * Since every `RecordType` now ships a packaged form-config (asserted by
 * `authorityTypeFormConfigCoverage.test.ts` here and
 * `PackagedFormConfigsValidationTest` backend-side), reaching this state means
 * the backend is unreachable or answering wrongly — a transient infrastructure
 * fault, which is exactly what should be shown and retried rather than papered
 * over. `useFormConfig` still logs the discriminator.
 *
 * <p>Since CZNA-1292 this is also what PERSONAL / CORPORATE / MEETING get on a
 * failed fetch. They used to render their bundled TS template instead, which
 * was the same wrong-form problem without even the error: the cataloguer worked
 * on a form the tenant had not configured and had no way to know.
 */
var FormConfigUnavailable = function (_a) {
    var recordType = _a.recordType, onCancel = _a.onCancel, embedded = _a.embedded;
    var footer = (React.createElement(PaneFooter, { renderStart: (React.createElement(Button, { onClick: onCancel },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.cancel" }))), renderEnd: (React.createElement(Button, { buttonStyle: "primary", onClick: function () { return window.location.reload(); } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.boundary.retry" }))) }));
    return (React.createElement(FormShell, { recordType: recordType, onCancel: onCancel, embedded: embedded, footer: footer },
        React.createElement("div", { "data-testid": "form-config-unavailable", style: { padding: '32px' } },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.configUnavailable", values: { type: React.createElement(FormattedMessage, { id: "ui-czna-authorities.recordType.".concat(recordType) }) } })))));
};
/**
 * Entry point — resolves the compiled form config (TASK-178) and hands it to
 * {@link ConfigDrivenProposalForm}, the single form engine.
 *
 * <p>Once loaded, `useFormConfig` returns `{ config: null }` whenever the
 * compiled config could not be resolved — 404, 403, 5xx, a network failure or
 * a 200 without `tabs` — for every type alike (CZNA-1292; until then the types
 * with a bundled TS template silently rendered that instead). Before CZNA-487
 * a null config routed to `LegacyForm`; it now routes to
 * {@link FormConfigUnavailable}.
 */
var ProposalForm = function (props) {
    var _a = useFormConfig(props.recordType, props.templateId), config = _a.config, isLoading = _a.isLoading;
    // CZNA-709 — 040 $a is the system's, so the form prefills the cataloguer's own library
    // sigla instead of an empty box with a placeholder; editable since CZNA-677. Overlaid on the compiled
    // config here rather than shipped in the template: the value is per-organization, so a
    // template constant would sign every library's records with NK's sigla. See
    // `withCatalogingSourceSigla` — and note the backend stamps the record either way, so
    // this is what NK sees, not what makes the data right.
    var _b = useAuthorizationContext(), context = _b.context, isContextLoading = _b.isLoading;
    var sigla = context === null || context === void 0 ? void 0 : context.organizationSigla;
    var founding = isFoundingForm(props);
    var effectiveConfig = useMemo(function () { return withCatalogingSourceSigla(config, sigla, founding); }, [config, sigla, founding]);
    // Show a loading skeleton during the first fetch (before react-query has
    // resolved success or failure). On stale-while-revalidate / cached hits
    // this branch is never visible — `isLoading` is `false` from render 0.
    //
    // A founding form also waits for the caller's sigla. Mounting it before the sigla and
    // remounting under the key below wiped whatever the cataloguer had typed in between —
    // the e2e suite hit exactly that while a freshly deployed mod-czna-users answered slowly
    // (CZNA-1291). The key stays as the fallback for a context that arrives anyway.
    if (isLoading || (founding && isContextLoading)) {
        return (React.createElement(FormConfigSkeleton, { recordType: props.recordType, onCancel: props.onCancel, embedded: props.embedded }));
    }
    if (effectiveConfig) {
        // The authorization context and form config are independent queries. On a cold load
        // the config can therefore mount the form before the caller's sigla arrives. RecordForm
        // intentionally treats its initial values as initial values, so merely replacing the
        // config afterwards would lock 040 $a while leaving its mounted value empty. Key the
        // founding form by the resolved sigla so that transition gets one clean initialization;
        // edit forms keep the stable no-sigla key because the overlay does not apply to them.
        var catalogingSourceKey = founding && (sigla === null || sigla === void 0 ? void 0 : sigla.trim())
            ? "cataloging-source-".concat(sigla.trim())
            : 'cataloging-source-none';
        return (React.createElement(ConfigDrivenProposalForm, __assign({ key: catalogingSourceKey }, props, { config: effectiveConfig })));
    }
    return (React.createElement(FormConfigUnavailable, { recordType: props.recordType, onCancel: props.onCancel, embedded: props.embedded }));
};
export default ProposalForm;
